import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, skip, takeUntil } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';

import { SystemService } from 'src/app/system.service';
import { CommunityService } from 'src/app/community.service';
import { UserService } from 'src/app/user.service';
import { BuildingService } from 'src/app/building.service';

import { NavPermissionsService, NavPermissions } from './nav-permissions.service';
import {
	COMMUNITY_OPTIONS,
	ACCOUNTING_OPTIONS,
	ADMINISTRATION_LIST,
	GLOBAL_ADMINISTRATION_LIST,
	SETTINGS_ADMINISTRATION_LIST,
	REQUESTS_ADMINISTRATION_LIST,
	FACILITIES_ADMINISTRATION_LIST,
	PM_UNITS_ITEM,
	CHART_OF_ACCOUNTS_ITEM,
	NavItem,
} from './configs/admin-menu.config';
import { CX_PROPERTY_GROUP_SUBMENU } from './configs/cx-menu.config';
import { Project } from 'models/project.models';
import { maptoarray } from 'utils/mapandarray';
import { AuthorizationService } from 'src/app/authorization.service';

export interface UrlState {
	showCommunity: boolean;
	showAccounting: boolean;
	showFacilities: boolean;
	showAdminOnly: boolean;
	showGlobalAdmin: boolean;
	showSettings: boolean;
	showRequests: boolean;
	isContactHome: boolean;
	communityOptionsHeader: string;
	accountingHeader: string;
	facilitiesHeader: string;
	administrationHeader: string;
	globalAdminHeader: string;
	settingsHeader: string;
	requestsHeader: string;
	currentCommunityURL: string;
	/** Raw URL segment for the community (id or link). Used to derive selectedProject. */
	communityRef: string | null;
	/** Third URL segment under /projects/{id}/{leaf} — used to resolve communityOptionsHeader from the full options list. */
	communityLeaf: string | null;
}

export interface NavState {
	permissions: NavPermissions;
	urlState: UrlState;
	items: MenuItem[];
	profileItems: MenuItem[];
	communityList: Project[];
	communityListLoading: boolean;
	selectedProject: Project | null;
	logoUrl: string;
	notificationsCount: number;
}

@Injectable()
export class NavMenuService implements OnDestroy {
	private destroy$ = new Subject<void>();
	private sessionCleanup$ = new Subject<void>();
	private initRun = 0;

	private permissionsReady$ = new BehaviorSubject<NavPermissions | null>( null );
	private communityList$ = new BehaviorSubject<Project[]>( [] );
	// Community list loads independently of the rest of the navbar (contact search, profile
	// dropdown, etc.) — this flag lets the template show a loading state for just that dropdown
	// instead of holding the whole navbar back on it.
	private communityListLoading$ = new BehaviorSubject<boolean>( false );
	private notificationsCount$ = new BehaviorSubject<number>( 0 );

	// For global search
	buildings: any[] = [];
	communities: Project[] = [];

	readonly state$: Observable<NavState>;
	readonly logoUrl$: Observable<string>;

	private readonly ss = inject( SystemService );
	private readonly communityService = inject( CommunityService );
	private readonly userService = inject( UserService );
	private readonly permissionsService = inject( NavPermissionsService );
	private readonly buildingService = inject( BuildingService );
	private readonly authorizationService = inject( AuthorizationService );

	constructor () {
		this.logoUrl$ = this.permissionsService.logoUrl$;

		const url$ = this.ss.url.pipe(
			filter( url => !!url ),
			distinctUntilChanged(),
			takeUntil( this.destroy$ ),
			shareReplay( 1 ),
		);

		// Pure synchronous URL → display-flags + communityRef mapping
		const urlState$ = url$.pipe(
			map( url => this.computeUrlState( url.split( '/' ).slice( 1 ) ) ),
			shareReplay( 1 ),
		);

		// Main reactive state — re-emits on any source change.
		// selectedProject is derived inline from urlState.communityRef + communityList
		// so it updates correctly when either the URL or the list changes.
		this.state$ = combineLatest( [
			this.permissionsReady$.pipe( filter( ( p ): p is NavPermissions => !!p ) ),
			urlState$,
			this.communityList$,
			this.communityListLoading$,
			this.notificationsCount$,
		] ).pipe(
			map( ( [permissions, urlState, communityList, communityListLoading, notificationsCount] ) => {
				const selectedProject = urlState.communityRef
					? ( communityList.find( c =>
						( c as any ).id === urlState.communityRef ||
						( c as any ).link === urlState.communityRef
					) ?? null )
					: null;
				const communityOptions = this.computeCommunityOptions( selectedProject );
				const accountingOptions = this.computeAccountingOptions( permissions, ( selectedProject as any )?.id );
				const communityOptionsHeader = urlState.communityLeaf
					? ( communityOptions.find( o => o.link === urlState.communityLeaf )?.name ?? COMMUNITY_OPTIONS[0].name )
					: COMMUNITY_OPTIONS[0].name;
				const resolvedUrlState = { ...urlState, communityOptionsHeader };
				return {
					permissions,
					urlState: resolvedUrlState,
					items: this.buildMenuItems( permissions, resolvedUrlState, communityOptions, accountingOptions ),
					profileItems: this.buildProfileItems( permissions ),
					communityList,
					communityListLoading,
					selectedProject,
					logoUrl: permissions.logoUrl,
					notificationsCount,
				} as NavState;
			} ),
			shareReplay( { bufferSize: 1, refCount: true } ),
			takeUntil( this.destroy$ ),
		);
	}

	async init (): Promise<void> {
		// Cancel subscriptions from any previous init() call (singleton service, re-used across logins)
		this.sessionCleanup$.next();

		this.permissionsService.reset();
		this.permissionsReady$.next( null );
		this.communityList$.next( [] );
		this.communityListLoading$.next( false );
		this.notificationsCount$.next( 0 );

		const permissions = await this.permissionsService.resolve();
		const run = ++this.initRun;

		// Pre-apply cached profileType before emitting so state$ only emits once
		const cachedUser = this.userService.user.getValue();
		if ( cachedUser ) {
			this.permissionsService.patchProfileType( cachedUser.profileType );
		}
		// Emit permissions right away — the rest of the navbar (contact search, profile
		// dropdown, etc.) only depends on this and must not wait on the community fetch below.
		this.permissionsReady$.next( this.permissionsService.current as NavPermissions );

		// Community list only needed for admin/employee. Fetched independently of the emit
		// above: the community dropdown shows its own loading state (communityListLoading)
		// until this resolves, instead of holding up the rest of the navbar.
		if ( !permissions.isContact && !permissions.isVendor ) {
			this.communityListLoading$.next( true );
			this.communityService.getCommunityArray()
				.then( list => { if ( run === this.initRun ) this.communityList$.next( this.formatCommunityList( list ) ); } )
				.catch( () => { /* community list stays empty */ } )
				.finally( () => { if ( run === this.initRun ) this.communityListLoading$.next( false ); } );
		}

		// Prefetch data for global search
		this.buildings = [];
		this.communities = [];
		this.buildingService.getAllbuildings().then( b => { if ( run === this.initRun ) this.buildings = maptoarray( b ); } );
		this.communityService.getAllCommunities().then( c => { if ( run === this.initRun ) this.communities = c; } );

		const untilSession$ = merge( this.destroy$, this.sessionCleanup$ );

		// Patch profileType for future user updates (skip(1) skips BehaviorSubject's current-value replay)
		this.userService.user.pipe(
			skip( 1 ),
			filter( u => !!u ),
			takeUntil( untilSession$ ),
		).subscribe( user => {
			this.permissionsService.patchProfileType( user.profileType );
			const patched = { ...this.permissionsReady$.getValue(), ...this.permissionsService.current };
			this.permissionsReady$.next( patched as NavPermissions );
		} );

		// Refresh community list when a new project is created
		this.communityService.createdProject.pipe(
			filter( v => !!v ),
			takeUntil( untilSession$ ),
		).subscribe( async () => {
			const refreshed = await this.communityService.getCommunityArray();
			if ( run === this.initRun ) this.communityList$.next( this.formatCommunityList( refreshed ) );
		} );

		// Trigger getUsers for admins with full access
		if ( this.ss.hasFullAccess && this.ss.currentUser?.isAdmin ) {
			this.userService.getUsers();
		}
	}

	setNotificationsCount ( count: number ): void {
		this.notificationsCount$.next( count );
	}

	reset (): void {
		this.permissionsService.reset();
		this.permissionsReady$.next( null );
		this.communityList$.next( [] );
		this.communityListLoading$.next( false );
	}

	get communityListSnapshot (): Project[] {
		return this.communityList$.getValue();
	}

	// ── Private helpers ──────────────────────────────────────────────────────

	private formatCommunityList ( list: Project[] ): Project[] {
		const mapped = list.map( e => ( {
			...e,
			name: e.name?.toLowerCase() !== ( e.mollakName || '' ).toLowerCase()
				? `${e.name}${e.mollakName ? ` (${e.mollakName}) ` : ''}`
				: e.name,
			icon: 'fa fa-building fa-lg',
		} ) );
		mapped.unshift( { name: 'Global View', icon: 'fa fa-globe fa-lg' } as any );
		return mapped;
	}

	private computeUrlState ( parts: string[] ): UrlState {
		const defaults: UrlState = {
			showCommunity: false,
			showAccounting: false,
			showFacilities: false,
			showAdminOnly: false,
			showGlobalAdmin: false,
			showSettings: false,
			showRequests: false,
			isContactHome: false,
			communityOptionsHeader: COMMUNITY_OPTIONS[0].name,
			accountingHeader: ACCOUNTING_OPTIONS[0].name,
			facilitiesHeader: FACILITIES_ADMINISTRATION_LIST[0].name,
			administrationHeader: ADMINISTRATION_LIST[0].name,
			globalAdminHeader: GLOBAL_ADMINISTRATION_LIST[0].name,
			settingsHeader: SETTINGS_ADMINISTRATION_LIST[0].name,
			requestsHeader: REQUESTS_ADMINISTRATION_LIST[0].name,
			currentCommunityURL: '',
			communityRef: null,
			communityLeaf: null,
		};

		const [section, sub, leaf, deep] = parts;

		if ( section === 'projects' && sub ) {
			let showAccounting = false;
			let showFacilities = false;
			let accountingHeader = ACCOUNTING_OPTIONS[0].name;
			let facilitiesHeader = FACILITIES_ADMINISTRATION_LIST[0].name;

			if ( leaf ) {
				if ( leaf === 'accounting' ) {
					showAccounting = true;
					if ( deep ) {
						const acc = ACCOUNTING_OPTIONS.find( o => o.link === `/accounting/${deep}` );
						if ( acc ) accountingHeader = acc.name;
					}
				} else if ( leaf === 'facilities' ) {
					showFacilities = true;
					if ( deep ) {
						const fac = FACILITIES_ADMINISTRATION_LIST.find( o => o.link === `/facilities/${deep}` );
						if ( fac ) facilitiesHeader = fac.name;
					}
				} else {
					// leaf is a direct project sub-route (e.g. 'budgets') — check if it belongs to accounting
					const accMatch = ACCOUNTING_OPTIONS.find( o => o.link === leaf );
					if ( accMatch ) {
						showAccounting = true;
						accountingHeader = accMatch.name;
					}
				}
			}

			return {
				...defaults,
				showCommunity: true,
				showAccounting,
				showFacilities,
				currentCommunityURL: `/projects/${sub}`,
				communityRef: sub,
				communityLeaf: leaf ?? null,
				accountingHeader,
				facilitiesHeader,
			};
		}

		if ( section === 'globalAdministration' ) {
			const matched = sub ? GLOBAL_ADMINISTRATION_LIST.find( o => o.link.split( '/' )[0] === sub ) : null;
			return { ...defaults, showGlobalAdmin: true, globalAdminHeader: matched?.name ?? GLOBAL_ADMINISTRATION_LIST[0].name };
		}

		if ( section === 'settings' ) {
			const matched = sub ? SETTINGS_ADMINISTRATION_LIST.find( o => o.link.split( '/' )[1] === sub ) : null;
			return { ...defaults, showAdminOnly: true, showSettings: true, settingsHeader: matched?.name ?? SETTINGS_ADMINISTRATION_LIST[0].name };
		}

		if ( section === 'request-admin' ) {
			const matched = sub ? REQUESTS_ADMINISTRATION_LIST.find( o => o.link.split( '/' )[1] === sub ) : null;
			return { ...defaults, showAdminOnly: true, showRequests: true, requestsHeader: matched?.name ?? REQUESTS_ADMINISTRATION_LIST[0].name };
		}

		if ( section === 'administration' ) {
			const matched = sub ? ADMINISTRATION_LIST.find( o => o.link.split( '/' )[1] === sub ) : null;
			return { ...defaults, showAdminOnly: true, administrationHeader: matched?.name ?? ADMINISTRATION_LIST[0].name };
		}

		// ADMINISTRATION_LIST items whose links don't start with 'administration/' live at their
		// own top-level routes (e.g. /users, /contacts). Keep the administration dropdown visible.
		const adminTopLevelMatch = ADMINISTRATION_LIST.find(
			option => !option.link.startsWith( 'administration' ) && option.link.split( '/' )[0] === section
		);
		if ( adminTopLevelMatch ) {
			return { ...defaults, showAdminOnly: true, administrationHeader: adminTopLevelMatch.name };
		}

		if ( section === 'welcome-customer' ) {
			return { ...defaults, isContactHome: true };
		}

		return defaults;
	}

	private computeCommunityOptions ( selectedProject: Project | null ): NavItem[] {
		const base = [...COMMUNITY_OPTIONS];
		if ( selectedProject?.isPM ) {
			base.splice( 5, 0, PM_UNITS_ITEM );
		}
		return base;
	}

	private computeAccountingOptions ( permissions: NavPermissions, communityId: string | undefined ): NavItem[] {
		const base = [...ACCOUNTING_OPTIONS];
		if ( !communityId ) return base;
		const hasAccess = permissions.viewChartOfAccountsAccessRights.some( cr => cr.refId === communityId );
		if ( hasAccess ) base.splice( 3, 0, CHART_OF_ACCOUNTS_ITEM );
		return base;
	}

	private toMenuItems ( list: NavItem[], base: string ): MenuItem[] {
		return list.map( item => ( {
			label: item.name,
			routerLink: base ? `/${base}/${item.link ?? item.id}` : `/${item.link ?? item.id}`,
		} ) );
	}

	private buildMenuItems (
		p: NavPermissions,
		u: UrlState,
		communityOptions: NavItem[],
		accountingOptions: NavItem[],
	): MenuItem[] {
		const cu = this.ss.currentUser;
		const isApproved = !!cu?.isApproved;
		const isApprovedAdmin = p.isAdmin || ( p.isEmployee && isApproved );
		const isApprovedClientAdmin = isApproved && !!this.authorizationService.isClientAdmin();
		const notDraft = !p.isDraftTenant;
		const contactNotHome = p.isContact && !u.isContactHome;
		const isLandlordCx = p.isLandlord && !p.isEmployee && !p.isAdmin && !this.authorizationService.isClientAdmin();
		const showCxItems = notDraft && ( contactNotHome || isLandlordCx );

		const notRestricted = !p.soaViewerContact && !p.epcContact;
		const inCommunity = u.showCommunity && !u.showAdminOnly;
		const inAdminSection = !u.showCommunity && u.showAdminOnly && !u.showGlobalAdmin;
		const inGlobalView = !u.showCommunity && !u.showAdminOnly && !u.showGlobalAdmin;
		const canSeeAdmin = notRestricted && inGlobalView && isApprovedClientAdmin;

		return [
			{
				label: 'Home',
				routerLink: p.homeRoute,
				// Exact, so Home isn't also marked on its sub-routes (e.g. Payments under /vendor-home)
				routerLinkActiveOptions: { exact: true },
				visible: notDraft && ( isApprovedAdmin || !u.isContactHome ),
			},
			{
				label: 'Payments',
				routerLink: '/vendor-home/payments',
				visible: p.isVendor,
			},
			// Community Options
			{
				label: u.communityOptionsHeader,
				visible: isApprovedAdmin && u.showCommunity,
				items: this.toMenuItems( communityOptions, u.currentCommunityURL ),
			},
			// Facilities
			{
				label: u.facilitiesHeader,
				visible: isApprovedAdmin && inCommunity && u.showFacilities,
				items: this.toMenuItems( FACILITIES_ADMINISTRATION_LIST, u.currentCommunityURL ),
			},
			// Accounting
			{
				label: u.accountingHeader,
				visible: isApprovedAdmin && u.showCommunity && u.showAccounting,
				items: this.toMenuItems( accountingOptions, u.currentCommunityURL ),
			},
			// Administration (collapsed — before entering admin section)
			{
				label: 'Administration',
				routerLink: '/administration',
				visible: canSeeAdmin,
			},
			// Administration (expanded)
			{
				label: u.administrationHeader,
				visible: notRestricted && inAdminSection && isApprovedClientAdmin,
				items: this.toMenuItems( ADMINISTRATION_LIST, '' ),
			},
			// Settings
			{
				label: u.settingsHeader,
				visible: inAdminSection && isApprovedAdmin && u.showSettings,
				items: this.toMenuItems( SETTINGS_ADMINISTRATION_LIST, '' ),
			},
			// Requests
			{
				label: u.requestsHeader,
				visible: inAdminSection && isApprovedAdmin && u.showRequests,
				items: this.toMenuItems( REQUESTS_ADMINISTRATION_LIST, '' ),
			},
			// Global Administration (collapsed)
			{
				label: 'Global Administration',
				routerLink: '/globalAdministration',
				visible: inGlobalView && p.isAdmin,
			},
			// Global Administration (expanded)
			{
				label: u.globalAdminHeader,
				visible: !u.showCommunity && !u.showAdminOnly && u.showGlobalAdmin && p.isAdmin,
				items: this.toMenuItems( GLOBAL_ADMINISTRATION_LIST, 'globalAdministration' ),
			},
			// My Units balance (landlord balance page)
			{ label: 'My Units', routerLink: '/my-units-balances', visible: notDraft && p.isLandlord },
			// My Property Group (tenant/landlord submenu)
			{
				label: 'My Property Group',
				visible: notDraft && ( p.isTenant || p.isLandlord ) && !p.isEmployee && !p.isAdmin,
				items: CX_PROPERTY_GROUP_SUBMENU,
			},
			// CX flat items
			// Exact, so it isn't also marked on Lost/Found (/my-requests/lostFound)
			{ label: 'My Requests', routerLink: '/my-requests', routerLinkActiveOptions: { exact: true }, visible: showCxItems },
			{ label: 'Event Calendar', routerLink: '/event-calendar', visible: showCxItems },
			{ label: 'My Units', routerLink: '/my-units', visible: showCxItems },
			{ label: 'Property Group Documents', routerLink: '/community-documents', visible: showCxItems },
			{ label: "Helpline & FAQ's", routerLink: '/helpline-faq', visible: showCxItems },
			{ label: 'Announcements', routerLink: '/community-wall', visible: showCxItems },
			{ label: 'Lost/Found', routerLink: '/my-requests/lostFound', visible: showCxItems && p.showLostFound },
			{ label: 'Tenders', routerLink: '/oc-tenders', visible: notDraft && contactNotHome && p.isOwnersCommitteeMember },
		];
	}

	private buildProfileItems ( p: NavPermissions ): MenuItem[] {
		const items: MenuItem[] = [
			{ label: 'Profile', routerLink: '/profile', icon: 'pi pi-user-edit' },
		];
		if ( p.isContact && p.showOnPortal ) {
			items.push( { label: 'Contact Us', routerLink: '/contact-us', icon: 'pi pi-info-circle' } );
		}
		items.push( { label: 'Sign Out', icon: 'pi pi-power-off' } );
		return items;
	}

	ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
